import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { partnerApi } from "../../services/partnerApi";
import { statusBadgeClass } from "../../components/instructor/InstructorShell";
import BrandMark from "../../components/BrandMark";
import Seo from "../../components/Seo";

const SCRAPE_STATUSES = ["pending", "queued", "in_progress", "ready", "failed", "paused"];

export default function AdminPartnerApplicationDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [scrapeNotes, setScrapeNotes] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError("");
      try {
        const res = await partnerApi.adminGet(id);
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function reload() {
    setError("");
    try {
      const res = await partnerApi.adminGet(id);
      setData(res);
    } catch (err) {
      setError(err.message);
    }
  }

  async function act(action) {
    setBusy(true);
    setError("");
    try {
      if (action === "claim") await partnerApi.adminClaim(id);
      if (action === "approve") await partnerApi.adminApprove(id);
      if (action === "reject") await partnerApi.adminReject(id, reason);
      if (action === "suspend") await partnerApi.adminSuspend(id, reason);
      if (action === "reinstate") await partnerApi.adminReinstate(id);
      setReason("");
      await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function setScrapeStatus(sourceId, status) {
    setBusy(true);
    setError("");
    try {
      await partnerApi.adminScrapeStatus(id, sourceId, {
        status,
        notes: scrapeNotes || undefined,
      });
      await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (!data && !error) {
    return (
      <div className="flex min-h-screen items-center justify-center text-gray-500">Loading…</div>
    );
  }

  const org = data?.org;
  const sources = data?.sources || [];
  const canApprove = sources.some((s) => s.scrape_status === "ready");

  return (
    <div className="min-h-screen bg-slate-50">
      <Seo
        title="Admin Partner Review"
        description="Review a partner application."
        canonicalPath={`/admin/partner-applications/${id}`}
        robots="noindex,nofollow"
      />
      <header className="border-b border-gray-100 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <BrandMark />
            <span className="font-semibold text-gray-900">Admin partner review</span>
          </div>
          <Link to="/admin/partner-applications" className="text-sm text-emerald-700 hover:underline">
            Back to queue
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}
        {org && (
          <div className="grid gap-6 lg:grid-cols-5">
            <div className="space-y-4 rounded-2xl border border-gray-100 bg-white p-6 lg:col-span-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex gap-3">
                  <div className="h-16 w-16 overflow-hidden rounded-xl border bg-gray-100">
                    {org.logo_url ? (
                      <img src={org.logo_url} alt="" className="h-full w-full object-contain" />
                    ) : null}
                  </div>
                  <div>
                    <h1 className="text-xl font-semibold text-gray-900">
                      {org.trade_name || org.legal_name}
                    </h1>
                    <p className="text-sm text-gray-500">Legal: {org.legal_name}</p>
                    <p className="text-sm text-gray-500">{org.email}</p>
                    <p className="text-sm text-gray-500">
                      {(() => {
                        const locs = Array.isArray(org.service_locations)
                          ? org.service_locations
                          : [];
                        if (locs.length) {
                          return locs
                            .map((loc) =>
                              [loc.city, loc.state, loc.country].filter(Boolean).join(", ")
                            )
                            .join(" · ");
                        }
                        return [org.city, org.state, org.country].filter(Boolean).join(", ");
                      })()}
                    </p>
                  </div>
                </div>
                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClass(org.status)}`}
                >
                  {org.status}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-gray-700">{org.about}</p>
              <div>
                <h2 className="mb-1 font-medium text-gray-900">Cancellation policy</h2>
                <p className="whitespace-pre-wrap text-sm text-gray-600">
                  {org.cancellation_policy || "—"}
                </p>
              </div>
              <div>
                <h2 className="mb-1 font-medium text-gray-900">Agreement</h2>
                <p className="text-sm text-gray-600">
                  {org.agreement_accepted_at
                    ? `Accepted ${org.agreement_version || ""} at ${org.agreement_accepted_at}`
                    : "Not accepted"}
                </p>
              </div>
              <div>
                <h2 className="mb-2 font-medium text-gray-900">Media</h2>
                <div className="flex flex-wrap gap-2">
                  {(data.assets || []).map((a) => (
                    <a
                      key={a.id}
                      href={a.public_url}
                      target="_blank"
                      rel="noreferrer"
                      className="block h-16 w-16 overflow-hidden rounded-lg border"
                    >
                      <img src={a.public_url} alt={a.kind} className="h-full w-full object-cover" />
                    </a>
                  ))}
                  {!data.assets?.length && <p className="text-sm text-gray-500">No media</p>}
                </div>
              </div>
            </div>

            <div className="space-y-4 lg:col-span-2">
              <div className="space-y-3 rounded-2xl border border-gray-100 bg-white p-6">
                <h2 className="font-semibold text-gray-900">Website sources & scrape</h2>
                <p className="text-xs text-gray-500">
                  Developers scrape manually (scraper/ → Cloud Run → Neon). Mark ready only when
                  listings look correct. Approve is blocked until then.
                </p>
                <textarea
                  className="min-h-[60px] w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  placeholder="Scrape notes (optional)"
                  value={scrapeNotes}
                  onChange={(e) => setScrapeNotes(e.target.value)}
                />
                {sources.map((s) => (
                  <div key={s.id} className="rounded-lg border border-gray-100 p-3 text-sm">
                    <a
                      href={s.website_url}
                      target="_blank"
                      rel="noreferrer"
                      className="break-all font-medium text-emerald-700 hover:underline"
                    >
                      {s.website_url}
                    </a>
                    <p className="mt-1 text-gray-500">Status: {s.scrape_status}</p>
                    {s.scrape_notes && (
                      <p className="mt-1 text-xs text-gray-500">{s.scrape_notes}</p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {SCRAPE_STATUSES.map((st) => (
                        <button
                          key={st}
                          type="button"
                          disabled={busy || s.scrape_status === st}
                          onClick={() => setScrapeStatus(s.id, st)}
                          className={`rounded px-2 py-1 text-xs font-medium ${
                            s.scrape_status === st
                              ? "bg-emerald-600 text-white"
                              : "bg-slate-100 text-gray-700 hover:bg-slate-200"
                          } disabled:opacity-50`}
                        >
                          {st}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {!sources.length && <p className="text-sm text-gray-500">No sources</p>}
              </div>

              <div className="space-y-3 rounded-2xl border border-gray-100 bg-white p-6">
                <h2 className="font-semibold text-gray-900">Actions</h2>
                <p className="text-xs text-gray-500">Score: {org.completeness_score}%</p>
                {!canApprove && (
                  <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    Approve disabled until at least one source is scrape_status=ready.
                  </p>
                )}
                <textarea
                  className="min-h-[80px] w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  placeholder="Reason required for reject / suspend"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <div className="flex flex-wrap gap-2">
                  {org.status === "Submitted" && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => act("claim")}
                      className="rounded-lg bg-amber-100 px-3 py-2 text-sm font-medium text-amber-900"
                    >
                      Claim / under review
                    </button>
                  )}
                  {["Submitted", "UnderReview"].includes(org.status) && (
                    <button
                      type="button"
                      disabled={busy || !canApprove}
                      onClick={() => act("approve")}
                      title={!canApprove ? "Mark scrape ready first" : undefined}
                      className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Approve
                    </button>
                  )}
                  {["Submitted", "UnderReview", "Approved"].includes(org.status) && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => act("reject")}
                      className="rounded-lg bg-red-100 px-3 py-2 text-sm font-medium text-red-800"
                    >
                      Reject / request changes
                    </button>
                  )}
                  {org.status === "Approved" && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => act("suspend")}
                      className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white"
                    >
                      Suspend
                    </button>
                  )}
                  {org.status === "Suspended" && (
                    <button
                      type="button"
                      disabled={busy || !canApprove}
                      onClick={() => act("reinstate")}
                      className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                    >
                      Reinstate
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
